import { motion } from 'framer-motion';

const cognitiveCapabilities = [
  { name: 'Reasoning', ai: 75, human: 95 },
  { name: 'Creativity', ai: 60, human: 95 },
  { name: 'Planning', ai: 65, human: 90 },
  { name: 'Common Sense', ai: 45, human: 95 },
  { name: 'Abstraction', ai: 55, human: 90 },
  { name: 'Learning', ai: 80, human: 85 },
  { name: 'Memory', ai: 90, human: 70 },
  { name: 'Metacognition', ai: 35, human: 80 },
];

export default function CognitionChart() {
  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
          AI vs Human <span className="text-cognition">Cognition</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          How current AI systems compare to human cognitive capabilities.
          The gap reveals both progress and remaining challenges.
        </p>
      </motion.div>

      {/* Cognition Radar Chart Image */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <img
          src="/images/Cognition_Radar_Chart.png"
          alt="AI vs Human Cognitive Capabilities"
          className="w-full max-w-2xl mx-auto rounded-xl border border-white/10"
        />
      </motion.div>

      {/* Capability Bars */}
      <div className="grid md:grid-cols-2 gap-6">
        {cognitiveCapabilities.map((cap, index) => (
          <motion.div
            key={cap.name}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
            className="p-4 rounded-xl bg-dark-800/50 border border-white/5"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-white">{cap.name}</h3>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-cognition" />
                  AI: {cap.ai}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-gray-400 border border-gray-300" />
                  Human: {cap.human}%
                </span>
              </div>
            </div>
            
            {/* Progress Bars */}
            <div className="space-y-2">
              {/* AI Bar */}
              <div className="relative h-3 bg-dark-700 rounded-full overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-cognition to-pink-400"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${cap.ai}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.05 }}
                />
              </div>
              {/* Human Bar */}
              <div className="relative h-2 bg-dark-700 rounded-full overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 rounded-full bg-gray-500"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${cap.human}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.05 + 0.1 }}
                  style={{ opacity: 0.5 }}
                />
                <div 
                  className="absolute inset-y-0 border-r-2 border-dashed border-gray-400"
                  style={{ left: `${cap.human}%` }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Insights */}
      <motion.div
        className="mt-12 p-6 rounded-xl bg-cognition/10 border border-cognition/20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h3 className="text-lg font-bold text-cognition mb-4">Key Insights</h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold text-white mb-2">🎯 AI Strengths</h4>
            <p className="text-gray-400">
              Memory, pattern recognition, and learning speed exceed human capabilities in specific domains.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">🔍 Key Gaps</h4>
            <p className="text-gray-400">
              Common sense, metacognition, and true understanding remain significant challenges.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">🚀 Rapid Progress</h4>
            <p className="text-gray-400">
              Reasoning and planning capabilities have improved dramatically with recent models.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
